/**
 * Notificações do sino (spec 037) — o contrato entre o SPA e a API.
 *
 * O que é notificação: um evento notificável (TICKET_EVENT_TO_NOTIFICATION_KEY)
 * num ticket que a pessoa enxerga — as mesmas mesas, o mesmo `view_others` e a
 * mesma ocultação da lista —, de um tipo que ela deixou ligado em Minha conta, e
 * que ela NÃO causou (o ator nunca é notificado da própria ação).
 *
 * Lido e não lido mora no SERVIDOR, por pessoa e por tenant (spec 037, item 1),
 * como o "visto" das Novidades (spec 036): o contador segue a pessoa entre
 * máquinas. Duas marcas:
 * - `readBefore`: "Marcar como lidas" lê tudo o que chegou até ali;
 * - a leitura avulsa: clicar numa notificação marca só ela.
 *
 * O `localStorage` não guarda nada disso (a marca antiga `erp.notifSeenAt` sai).
 */
import { z } from 'zod';
import { Dateish, PG_INT_MAX } from './common';
import { NOTIFICATION_EVENT_KINDS, type NotificationEventKind } from './notification-prefs';
import { TicketEventTypeSchema } from './tickets';
import { CommentAuthorKindSchema } from './work-views';

/**
 * O contador vem do servidor contado até aqui: 100 ou mais aparece "99+". O
 * servidor para de contar no teto — a consulta não percorre o histórico inteiro
 * para um número que a tela não mostraria.
 */
export const NOTIFICATIONS_UNREAD_CAP = 100;

/** "99+" a partir do teto; o número puro abaixo dele. */
export function unreadBadge(count: number): string {
  return count >= NOTIFICATIONS_UNREAD_CAP ? `${NOTIFICATIONS_UNREAD_CAP - 1}+` : String(count);
}

/** linhas do sino; a página pede até 50 por vez */
export const NOTIFICATIONS_BELL_SIZE = 20;
export const NOTIFICATIONS_PAGE_SIZE_MAX = 50;
/** quantas notificações um "marcar como lida" leva de uma vez */
export const NOTIFICATIONS_READ_BATCH_MAX = 50;

/**
 * Chave de uma notificação: o tipo da fonte e o id dela. `e:` = evento de
 * ticket (ticket_events); `m:` = menção na comunicação interna
 * (ticket_comment_mentions, PR C). A chave é opaca para o SPA: ele a recebe na
 * linha e a devolve ao marcar como lida.
 */
export const NOTIFICATION_KEY_PATTERN = /^[em]:[1-9][0-9]{0,9}$/;
export const NotificationItemKeySchema = z
  .string()
  .regex(NOTIFICATION_KEY_PATTERN)
  .refine((k) => Number(k.slice(2)) <= PG_INT_MAX, 'id fora do int4');

export type NotificationSourceKind = 'event' | 'mention';

/** decompõe a chave validada (o formato já passou pelo schema) */
export function parseNotificationKey(key: string): { kind: NotificationSourceKind; id: number } | null {
  if (!NOTIFICATION_KEY_PATTERN.test(key)) return null;
  const id = Number(key.slice(2));
  if (id > PG_INT_MAX) return null;
  return { kind: key.startsWith('m:') ? 'mention' : 'event', id };
}

export const notificationEventKey = (eventId: number) => `e:${eventId}`;
export const notificationMentionKey = (mentionId: number) => `m:${mentionId}`;

/**
 * filtro por tipo da página /notificacoes: os eventos da tabela de preferências
 * (a comunicação interna separada da pública, PR B)
 */
export const NotificationTypeFilterSchema = z.enum(NOTIFICATION_EVENT_KINDS);

export const NOTIFICATION_READ_STATES = ['all', 'unread', 'read'] as const;
export const NotificationReadStateSchema = z.enum(NOTIFICATION_READ_STATES);
export type NotificationReadState = z.infer<typeof NotificationReadStateSchema>;

/** GET /notifications — o sino (sem total) e a página (com total) */
export const NotificationsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  per_page: z.coerce.number().int().min(1).max(NOTIFICATIONS_PAGE_SIZE_MAX).default(NOTIFICATIONS_BELL_SIZE),
  type: NotificationTypeFilterSchema.optional(),
  state: NotificationReadStateSchema.default('all'),
  /** `1` = conta o total do filtro (a página precisa; o sino não paga essa conta) */
  total: z
    .enum(['0', '1', 'true', 'false'])
    .transform((v) => v === '1' || v === 'true')
    .default(false),
});
export type NotificationsQuery = z.infer<typeof NotificationsQuerySchema>;

export const NotificationItemSchema = z.object({
  key: z.string(),
  /** a fonte: evento do ticket ou menção (PR C) */
  kind: z.enum(['event', 'mention']),
  ticketNumber: z.number().int(),
  ticketTitle: z.string(),
  deskName: z.string(),
  /** o tipo do evento do ticket; null na menção */
  eventType: TicketEventTypeSchema.nullable(),
  /** o evento da tabela de preferências que governa a linha (o rótulo do tipo) */
  prefKey: z.enum(NOTIFICATION_EVENT_KINDS),
  /** quem fez — nunca a própria pessoa; null = sistema (automação, espelho, e-mail) */
  actorName: z.string().nullable(),
  /** comunicação: interna (só a equipe) ou não; ausente nos demais tipos */
  internal: z.boolean().optional(),
  createdAt: Dateish,
  read: z.boolean(),
});
export type NotificationItem = z.infer<typeof NotificationItemSchema>;

export const NotificationsPageSchema = z.object({
  items: z.array(NotificationItemSchema),
  /** há mais páginas depois desta (o sino não pede o total) */
  hasMore: z.boolean(),
  /** só com `total=1` */
  total: z.number().int().nullable(),
  /** não lidas de TODOS os tipos ligados (não do filtro), até NOTIFICATIONS_UNREAD_CAP */
  unread: z.number().int(),
});
export type NotificationsPage = z.infer<typeof NotificationsPageSchema>;

export const NotificationsReadBodySchema = z
  .object({ keys: z.array(NotificationItemKeySchema).min(1).max(NOTIFICATIONS_READ_BATCH_MAX) })
  .strict();
export type NotificationsReadBody = z.infer<typeof NotificationsReadBodySchema>;

/**
 * "Marcar como lidas": até `before` (a notificação mais nova que a pessoa tinha
 * na tela), para que a que chegou depois de abrir o sino continue não lida. Sem
 * `before`, até agora. O servidor nunca aceita um instante no futuro.
 */
export const NotificationsReadAllBodySchema = z
  .object({ before: z.iso.datetime({ offset: true }).optional() })
  .strict();
export type NotificationsReadAllBody = z.infer<typeof NotificationsReadAllBodySchema>;

/** resposta das duas escritas: o contador novo, contado pelo servidor */
export const NotificationsUnreadSchema = z.object({ unread: z.number().int() });
export type NotificationsUnread = z.infer<typeof NotificationsUnreadSchema>;

/**
 * "Últimas respostas" (aba do sino): as comunicações mais recentes nos tickets
 * DA PESSOA — os tickets em que ela é a responsável —, de qualquer autor que não
 * ela, públicas e internas. É uma lista, sem lido e não lido.
 */
export const NOTIFICATIONS_REPLIES_SIZE = 20;
export const NOTIFICATION_REPLY_EXCERPT_MAX = 200;

export const NotificationReplySchema = z.object({
  commentId: z.number().int(),
  ticketNumber: z.number().int(),
  ticketTitle: z.string(),
  deskName: z.string(),
  authorName: z.string(),
  authorKind: CommentAuthorKindSchema,
  internal: z.boolean(),
  /** o começo do texto, em uma linha */
  excerpt: z.string(),
  createdAt: Dateish,
});
export type NotificationReply = z.infer<typeof NotificationReplySchema>;

/** GET /notifications/replies */
export const NotificationRepliesSchema = z.object({ items: z.array(NotificationReplySchema) });
export type NotificationReplies = z.infer<typeof NotificationRepliesSchema>;

/** o rótulo pt-BR de cada tipo (sino, página, aviso do navegador) */
export const NOTIFICATION_TYPE_LABELS: Record<NotificationEventKind, string> = {
  ticket_created: 'Novo ticket',
  comment_added: 'Nova comunicação',
  internal_comment_added: 'Nova comunicação interna',
  mention: 'Menção na comunicação interna',
  stage_changed: 'Mudança de estágio',
  status_changed: 'Mudança de status',
  responsible_changed: 'Mudança de responsável',
  appointment_added: 'Novo apontamento',
};

/** o rótulo da linha: comunicação interna se diz interna */
export function notificationTypeLabel(item: Pick<NotificationItem, 'prefKey' | 'internal'>): string {
  if (item.prefKey === 'comment_added' && item.internal === true) return 'Nova comunicação interna';
  return NOTIFICATION_TYPE_LABELS[item.prefKey];
}

/**
 * O aviso do sistema operacional no padrão do TiFlux: o tipo no título; o
 * número, o título do ticket, a mesa e o autor no corpo. Puro, para a guarda.
 * `deskTerm` é o termo do tenant para "mesa" (spec 017).
 */
export function desktopNotificationText(
  item: Pick<NotificationItem, 'prefKey' | 'internal' | 'ticketNumber' | 'ticketTitle' | 'deskName' | 'actorName'>,
  more = 0,
  deskTerm = 'mesa'
): { title: string; body: string } {
  const title = `${notificationTypeLabel(item)}${more > 0 ? ` (+${more})` : ''}`;
  const lines = [`#${item.ticketNumber} — ${item.ticketTitle}`];
  if (item.deskName) lines.push(`${deskTerm.charAt(0).toUpperCase()}${deskTerm.slice(1)}: ${item.deskName}`);
  if (item.actorName) lines.push(`Por: ${item.actorName}`);
  return { title, body: lines.join('\n') };
}
